// Firebase configuration for authentication
import { initializeApp } from 'firebase/app';
import { getAuth, GoogleAuthProvider, signInWithPopup, getRedirectResult, User, UserCredential } from 'firebase/auth';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';

// Firebase configuration from environment variables
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);

// Configure auth provider
const provider = new GoogleAuthProvider();
// Always prompt user to select account
provider.setCustomParameters({ prompt: 'select_account' });

/**
 * Sign in with Google using the appropriate method for the platform
 * Uses popup for web and a modified approach for mobile
 * @returns A Promise that resolves with the user credentials
 */
export const signInWithGoogle = async (): Promise<UserCredential> => {
  const isNative = Capacitor.isNativePlatform();
  
  if (isNative) {
    try {
      console.log('Starting mobile Google sign-in');
      
      // Use the full path to our custom auth handler
      const authUrl = `https://${firebaseConfig.authDomain}/auth/handler`;
      
      // console.log('Opening browser with auth URL:', authUrl);
      
      // Open the in-app browser
      await Browser.open({
        url: authUrl,
        presentationStyle: 'fullscreen' // Use fullscreen for better experience
      });
      
      console.log('Browser opened successfully');
      
      // Return a placeholder since the actual auth will be handled separately
      // The user will need to manually return to the app after authentication
      // They'll click the "Return to App" button on the success page
      return new Promise((resolve) => {
        setTimeout(() => {
          // console.log('Mobile auth flow initiated - user will complete in browser');
          // Return a mock UserCredential object to maintain the expected return type
          resolve({
            user: null as any,
            providerId: 'google.com',
            operationType: 'signIn'
          } as unknown as UserCredential);
        }, 500);
      });
    } catch (error) {
      console.error('Mobile auth error:', error);
      throw error instanceof Error ? error : new Error(String(error));
    }
  } else {
    // On web, use popup (this works fine)
    console.log('Using popup sign-in for web');
    return signInWithPopup(auth, provider);
  }
};

/**
 * Check for pending redirect results from Google authentication
 * This should be called on app startup to handle authentication redirects
 */
export const checkRedirectResult = async (): Promise<UserCredential | null> => {
  try {
    console.log("Checking for redirect results");
    const result = await getRedirectResult(auth);
    console.log("Redirect result:", result ? "Found" : "None");
    return result;
  } catch (error) {
    console.error('Error checking redirect result:', error);
    return null;
  }
};

/**
 * Sign out the current user
 * @returns A Promise that resolves when the user is signed out
 */
export const signOut = () => auth.signOut();

export { auth };
export type { User };